import React, { useMemo, useState } from 'react';
import { Table, Tag, Space, Button, Tooltip, Typography, Drawer, Descriptions } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import useGetAuditLogs from '../hooks/useGetAuditLogs';
import { AuditLogSchema } from '../types/audit-log';
import TableWrapper from '../components/common/TableWrapper';
import FilterDrawer from '../components/common/FilterDrawer';

const AuditLogs: React.FC = () => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLogSchema | null>(null);
  const [actorEmail, setActorEmail] = useState<string>('');
  const [context, setContext] = useState<string>('');
  const [range, setRange] = useState<any[]>([]);

  const queryParams = {
    actor_email: actorEmail || undefined,
    context: context || undefined,
    from: Array.isArray(range) && range[0] ? Math.floor(range[0].valueOf()) : undefined,
    to: Array.isArray(range) && range[1] ? Math.floor(range[1].valueOf()) : undefined,
  };

  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, refetch } = useGetAuditLogs(queryParams);

  const columns = useMemo(
    () => [
      {
        title: 'Timestamp',
        dataIndex: 'timestamp',
        key: 'timestamp',
        width: 180,
        render: (value: string) => {
          if (!value) return '';
          // Handle both string timestamps and numeric timestamps
          let ts = typeof value === 'string' ? parseInt(value, 10) : value;
          if (!ts || Number.isNaN(ts)) return value; // Return original string if can't parse
          
          // Check if timestamp is in seconds (less than year 2100) or milliseconds
          // If timestamp is less than year 2100 in seconds, it's likely in seconds
          // If timestamp is greater than year 2100 in seconds, it's likely in milliseconds
          const year2100InSeconds = 4102444800; // January 1, 2100 in seconds
          if (ts < year2100InSeconds) {
            // Timestamp is in seconds, convert to milliseconds
            ts = ts * 1000;
          }
          
          return new Date(ts).toLocaleString();
        },
      },
      {
        title: 'User',
        key: 'actor',
        width: 200,
        render: (_: any, record: AuditLogSchema) => (
          <Space direction="vertical" size={0}>
            <Tooltip title={`${record?.actor_firstname || ''} ${record?.actor_lastname || ''}`}>
              <Typography.Text
                style={{ maxWidth: 180, display: 'inline-block' }}
                ellipsis
              >
                {`${record?.actor_firstname || ''} ${record?.actor_lastname || ''}`.trim()}
              </Typography.Text>
            </Tooltip>
            <Tooltip title={record?.actor_email || ''}>
              <Typography.Text
                style={{ maxWidth: 180, display: 'inline-block', color: '#8c8c8c' }}
                ellipsis
              >
                {record?.actor_email || ''}
              </Typography.Text>
            </Tooltip>
          </Space>
        ),
      },
      {
        title: 'Action',
        dataIndex: 'action',
        key: 'action',
        width: 150,
        render: (value: any) => (
          <Tooltip title={String(value ?? '')}>
            <Tag color="blue" style={{ maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <Typography.Text ellipsis style={{ maxWidth: 110, display: 'inline-block' }}>
                {String(value ?? '')}
              </Typography.Text>
            </Tag>
          </Tooltip>
        ),
      },
      {
        title: 'Context',
        dataIndex: 'action_context',
        key: 'action_context',
        width: 150,
        render: (value: any) => (
          <Tooltip title={String(value ?? '')}>
            <Typography.Text ellipsis style={{ maxWidth: 130, display: 'inline-block' }}>
              {String(value ?? '')}
            </Typography.Text>
          </Tooltip>
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 80,
        fixed: 'right' as const,
        render: (_: any, record: AuditLogSchema) => (
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedLog(record);
              setDetailsOpen(true);
            }}
            size="small"
            style={{ cursor: 'pointer' }}
          >
            View
          </Button>
        ),
      },
    ],
    [/* deps */]
  );

  return (
    <div className="tabled">
      <div className="card">
        <div className="header-table">
          <h5 className="mb-0">Audit Logs</h5>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <Button onClick={() => setFiltersOpen(true)}>Filter</Button>
            <Button 
              onClick={() => {
                setActorEmail('');
                setContext('');
                setRange([]);
                refetch();
              }} 
              loading={isFetching}
            >
              Refresh
            </Button>
          </div>
        </div>

        <TableWrapper>
          <Table
            rowKey={(row) => row.id}
            columns={columns as any}
            dataSource={data as any}
            pagination={false}
            loading={isFetching && !isFetchingNextPage}
            scroll={{ x: 800 }}
            onScroll={(e: any) => {
              const target = e.currentTarget;
              if (hasNextPage && target.scrollTop + target.clientHeight >= target.scrollHeight - 50) {
                if (!isFetchingNextPage) {
                  fetchNextPage();
                }
              }
            }}
            components={{
              body: {
                cell: (props: any) => (
                  <td {...props} style={{ ...props.style, cursor: 'pointer' }} />
                ),
              },
            }}
          />
        </TableWrapper>
      </div>
      <FilterDrawer
        visible={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        onFinish={(values: any) => {
          setActorEmail(values.actor_email || '');
          setContext(values.context || '');
          setRange(values.range || []);
          setFiltersOpen(false);
        }}
        initialValues={{ actor_email: actorEmail, context, range }}
        fields={[
          { type: 'text', name: 'actor_email', label: 'User Email', placeholder: 'user@example.com' },
          { type: 'text', name: 'context', label: 'Context', placeholder: 'e.g. Transactions' },
          { 
            type: 'date-range', 
            name: 'range', 
            label: 'Date range', 
            showTime: true,
            disabledDate: (current: any) => {
              if (!current) return false;
              const today = new Date();
              today.setHours(23, 59, 59, 999);
              return current.valueOf() > today.valueOf();
            }
          },
        ] as any}
      />
      
      <Drawer
        title="Audit Log Details"
        placement="right"
        width={800}
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
      >
        {selectedLog && (
          <div>
            <Descriptions column={1} bordered>
              <Descriptions.Item label="Timestamp">
                {(() => {
                  const ts = parseInt(selectedLog.timestamp, 10);
                  if (!ts || Number.isNaN(ts)) return selectedLog.timestamp;
                  
                  // Check if timestamp is in seconds or milliseconds
                  const year2100InSeconds = 4102444800; // January 1, 2100 in seconds
                  const finalTs = ts < year2100InSeconds ? ts * 1000 : ts;
                  
                  return new Date(finalTs).toLocaleString();
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="User">
                {`${selectedLog.actor_firstname} ${selectedLog.actor_lastname}`.trim()}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {selectedLog.actor_email}
              </Descriptions.Item>
              <Descriptions.Item label="Action">
                <Tag color="blue">{selectedLog.action}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Context">
                {selectedLog.action_context}
              </Descriptions.Item>
              <Descriptions.Item label="IP Address">
                <Typography.Text code>{selectedLog.actor_ip}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="User Agent">
                <Typography.Text style={{ fontSize: '12px', wordBreak: 'break-all' }}>
                  {selectedLog.actor_ua}
                </Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="User ID">
                <Typography.Text code>{selectedLog.user_id}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="Profile ID">
                <Typography.Text code>{selectedLog.profile_id}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="Meta Data">
                <pre style={{ 
                  background: '#f5f5f5', 
                  padding: '8px', 
                  borderRadius: '4px',
                  fontSize: '12px',
                  maxHeight: '200px',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {(() => {
                    try {
                      // If it's already an object, stringify it directly
                      if (typeof selectedLog.action_meta === 'object') {
                        return JSON.stringify(selectedLog.action_meta, null, 2);
                      }
                      // If it's a string, try to parse it first
                      const parsed = JSON.parse(selectedLog.action_meta);
                      return JSON.stringify(parsed, null, 2);
                    } catch {
                      // If all else fails, convert to string
                      return String(selectedLog.action_meta || '');
                    }
                  })()}
                </pre>
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default AuditLogs;


